<template>
  <modal-vue
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    @close="handleClose"
    :max-width="currentStep === STEP.PREVIEW ? 'max-w-[1000px]' : 'max-w-[500px]'"
    class="register-upload-modal"
  >
    <template #header>
      <div class="flex items-center justify-between w-full">
        <h2 class="text-xl font-bold text-textPrimary">{{ stepTitle }}</h2>
      </div>
    </template>

    <!--
      M2/§8.2 — quando o wizard é reaberto pelo reencaminhamento do "Enviar registro", os
      campos que vêm da vaga chegam preenchidos (`initialForm`) e bloqueados (`lockedFields`).
      Sem as duas props nada muda: o wizard de folha é exatamente o de hoje.
    -->
    <StepForm
      v-if="currentStep === STEP.FORM"
      :model-value="form"
      :locked-fields="lockedFields"
      @update:model-value="(val) => Object.assign(form, val)"
      v-model:valid="isStepFormValid"
    />

    <StepFile v-else-if="currentStep === STEP.FILE" v-model="selectedFile" />

    <StepPreview
      v-else-if="currentStep === STEP.PREVIEW"
      :file="selectedFile"
      v-model:rotation="rotation"
      v-model:zoom="zoomScale"
      :monitoring-type-label="form.monitoringTypeLabel ?? ''"
      @update:total-pages="attachedPages = $event"
    />

    <!--
      Eixo 2A da discovery 2026-09-11 — checklist antes do POST, só nas modalidades de folha.
      `INCOMPLETE_SHEET` é a rejeição mais comum em PROD e cada caso custa um ciclo de ida e
      volta com a unidade. Nenhum item é obrigatório: o botão é "Enviar assim mesmo".
    -->
    <StepChecklist
      v-else-if="currentStep === STEP.CHECKLIST"
      v-model="selfCheckState"
      :formatted-date="formattedReferenceDate"
      :health-unit-id="healthUnitIdOrNull"
      :schedule-ids="selectedScheduleIdsAsNumbers"
      :reference-date="formatDateToISO(form.date)"
      :pages="attachedPages"
    />

    <!--
      Eixo 2B — avisos da pré-validação de imagem. Nunca bloqueiam. Aparecem ANTES do envio
      (pré-checagem, #3083); depois dele, só se a pré-checagem não rodou.
    -->
    <StepWarnings
      v-else-if="currentStep === STEP.WARNINGS"
      :warnings="qualityWarnings"
      :phase="warningsPhase"
    />

    <!--
      #3072 — o registro da data foi reprovado e não tem substituto: a mesma folha segue como
      NOVA FOLHA desse registro, com justificativa, em vez de travar no erro.
    -->
    <StepNewSheet
      v-else-if="currentStep === STEP.NEW_SHEET"
      v-model="newSheetJustification"
      :formatted-date="formattedReferenceDate"
      :targets="newSheetTargets.map((target) => target.scheduleName)"
      :has-other-schedules="(pendingUploadSchedules?.length ?? 0) > 0"
    />

    <template #footer>
      <div
        class="flex items-center w-full mt-2"
        :class="isAfterSendWarnings ? 'justify-end' : 'justify-between'"
      >
        <!-- Depois do envio não há "Refazer a foto": o registro já tem folha (#3083). -->
        <outlined-button
          v-if="!isAfterSendWarnings"
          data-testid="upload-modal-voltar-button"
          :text="backButtonText"
          width="160px"
          @click="handleBackAction"
          class="!border-primary30 !text-primary30 !font-bold"
        />

        <filled-button
          data-testid="upload-modal-confirmar-button"
          :text="mainButtonText"
          width="180px"
          @click="handleMainAction"
          :loading="isSaving"
          :disabled="
            (currentStep === STEP.FORM && !isStepFormValid) ||
            (currentStep === STEP.FILE && !selectedFile) ||
            (currentStep === STEP.NEW_SHEET && !isNewSheetJustificationValid)
          "
          class="!bg-primary !text-white !font-bold hover:!bg-primary40"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { ImageState, DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import Network from '@/Network'
import type { DsAttendanceUploadForm } from './types'
import type {
  DSAttendanceUploadExtras,
  ScheduleShiftsDto
} from '@/Network/Types/Requests/DsAttendanceRequest'
import type { DSAttendanceUploadResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import StepForm from './StepForm.vue'
import StepFile from './StepFile.vue'
import StepPreview from './StepPreview.vue'
import StepChecklist from './StepChecklist.vue'
import StepWarnings from './StepWarnings.vue'
import StepNewSheet from './StepNewSheet.vue'
import { buildSelfCheckPayload, emptySelfCheckState } from './self-check'
import {
  NEW_SHEET_MIN_JUSTIFICATION,
  rejectedContainerIdFrom,
  splitSelectionForNewSheet
} from './rejected-sheet'

interface Props {
  modelValue: boolean
  /**
   * Campos que chegam da vaga de origem (M2, §8.2). O modal os usa como semente
   * do formulário a cada abertura; ausente, o wizard nasce vazio como sempre.
   */
  initialForm?: Partial<DsAttendanceUploadForm> | null
  /** Quais desses campos ficam bloqueados no passo 1. Vazio = tudo editável. */
  lockedFields?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  initialForm: null,
  lockedFields: () => []
})
const emit = defineEmits(['update:modelValue', 'save'])

/**
 * Passos nomeados: o fluxo passou de 3 para 5 com a qualidade na origem (Eixo 2 da discovery
 * 2026-09-11), e `currentStep === 3` espalhado pelo template já não dizia qual passo era.
 * CHECKLIST só existe nas modalidades de folha; WARNINGS só quando o backend devolve avisos.
 * NEW_SHEET só quando o registro da data foi reprovado e a folha pode entrar como nova (#3072).
 */
const STEP = {
  FORM: 1,
  FILE: 2,
  PREVIEW: 3,
  CHECKLIST: 4,
  WARNINGS: 5,
  NEW_SHEET: 6
} as const

const currentStep = ref<number>(STEP.FORM)
const isSaving = ref(false)

const selectedFile = ref<File | null>(null)
const rotation = ref(0)
const zoomScale = ref(1)

/** Páginas anexadas — 1 para imagem, total do PDF quando o renderizador as conta. */
const attachedPages = ref(1)
const selfCheckState = ref(emptySelfCheckState())
const qualityWarnings = ref<string[]>([])

/**
 * #3083 — os avisos de qualidade aparecem ANTES do envio, pela pré-checagem: aí "Refazer a
 * foto" é seguro, porque nada foi gravado. Depois do envio o registro já tem folha e refazer
 * cairia sempre no 409 de folha já enviada — o painel `AFTER_SEND` só existe quando a
 * pré-checagem não conseguiu rodar, e é apenas informativo.
 */
type WarningsPhase = 'BEFORE_SEND' | 'AFTER_SEND'
const warningsPhase = ref<WarningsPhase>('BEFORE_SEND')
/** A pré-checagem respondeu (com ou sem avisos)? Então o emissor já viu o que havia para ver. */
const qualityPrecheckRan = ref(false)
const isAfterSendWarnings = computed(
  () => currentStep.value === STEP.WARNINGS && warningsPhase.value === 'AFTER_SEND'
)

/**
 * #3072 — registros reprovados que recebem a folha como nova folha (`sent` marca os já enviados,
 * para "tentar de novo" não repetir), os pares que seguem pelo upload normal (`null` = ainda não
 * houve separação) e se esse upload já passou. Uma folha costuma cobrir várias escalas.
 */
interface NewSheetTarget {
  containerId: number
  scheduleName: string
  sent: boolean
}
const newSheetTargets = ref<NewSheetTarget[]>([])
const pendingUploadSchedules = ref<ScheduleShiftsDto[] | null>(null)
const normalUploadDone = ref(false)
const newSheetJustification = ref('')
const isNewSheetJustificationValid = computed(
  () => newSheetJustification.value.trim().length >= NEW_SHEET_MIN_JUSTIFICATION
)

const shiftStore = useShiftStore()
const { listShifts } = storeToRefs(shiftStore)

const getDefaultForm = (): DsAttendanceUploadForm => ({
  uploadedBy: null,
  company: null,
  healthUnit: null,
  schedules: [],
  shifts: [],
  date: null,
  imageState: ImageState.PENDING,
  uploadAnother: false,
  monitoringType: '',
  monitoringTypeLabel: ''
})

/**
 * Semente do formulário: o default de sempre, sobrescrito pelo que a vaga já
 * sabe. Cópia por spread — `initialForm` é do chamador e não pode ser mutado
 * pelos campos que o emissor ainda vai preencher.
 *
 * `company`/`healthUnit` chegam como número (id da API) de uns chamadores e como
 * string (value das stores) de outros. É de propósito: a tolerância vive no
 * `value-comparator` dos selects do `StepForm`, não numa coerção aqui.
 */
const buildInitialForm = (): DsAttendanceUploadForm => ({
  ...getDefaultForm(),
  ...(props.initialForm ?? {})
})

const form = ref<DsAttendanceUploadForm>(buildInitialForm())

const handleClose = () => {
  emit('update:modelValue', false)
  setTimeout(resetModal, 300)
}

const resetModal = () => {
  currentStep.value = STEP.FORM
  selectedFile.value = null
  rotation.value = 0
  zoomScale.value = 1
  attachedPages.value = 1
  selfCheckState.value = emptySelfCheckState()
  qualityWarnings.value = []
  warningsPhase.value = 'BEFORE_SEND'
  qualityPrecheckRan.value = false
  newSheetTargets.value = []
  pendingUploadSchedules.value = null
  normalUploadDone.value = false
  newSheetJustification.value = ''
  form.value = buildInitialForm()
}

/**
 * Modalidades de folha física: são as que têm páginas para conferir e as que produzem
 * `INCOMPLETE_SHEET`. App externo e app Minha Escala não passam pelo checklist.
 */
/**
 * Checklist do emissor só na FOLHA diária: `POST /upload` é o único endpoint que aceita
 * `selfCheck`/`expectedPages`/`qualityWarnings`. Numa folha não diária o passo coletaria uma
 * declaração que o backend descarta.
 *
 * Fase 3: estender `upload-non-daily` com os mesmos campos e devolver `NON_DAILY_SHEET` aqui.
 */
const needsSelfCheck = computed(
  () => form.value.monitoringType === DSAttendanceMonitoringType.FOLHA
)

const stepTitle = computed(() => {
  switch (currentStep.value) {
    case STEP.FORM:
      return 'Upload de Registro de Plantão'
    case STEP.FILE:
      return 'Selecione o Arquivo'
    case STEP.PREVIEW:
      return 'Confirme o Arquivo'
    case STEP.CHECKLIST:
      return 'Antes de enviar, confira a folha'
    case STEP.NEW_SHEET:
      return 'Folha reprovada: enviar nova folha'
    default:
      return warningsPhase.value === 'BEFORE_SEND'
        ? 'Confira a qualidade da foto'
        : 'Avisos da folha enviada'
  }
})

const mainButtonText = computed(() => {
  if (currentStep.value === STEP.CHECKLIST) return 'Enviar assim mesmo'
  if (currentStep.value === STEP.NEW_SHEET) return 'Enviar nova folha'
  if (currentStep.value === STEP.WARNINGS) {
    return warningsPhase.value === 'BEFORE_SEND' ? 'Continuar assim mesmo' : 'Concluir'
  }
  return currentStep.value < STEP.PREVIEW ? 'Próximo' : 'Salvar'
})

const backButtonText = computed(() => {
  if (currentStep.value === STEP.CHECKLIST) return 'Voltar e corrigir'
  if (currentStep.value === STEP.WARNINGS) return 'Refazer a foto'
  const closesModal = currentStep.value === STEP.FORM || currentStep.value === STEP.NEW_SHEET
  return closesModal ? 'Cancelar' : 'Voltar'
})

const handleBackAction = () => {
  // Nada foi gravado: o upload recusou e a nova folha ainda não foi enviada.
  if (currentStep.value === STEP.NEW_SHEET) {
    handleClose()
    return
  }

  // "Refazer a foto" (só antes do envio — depois o botão nem aparece): nada foi gravado, então
  // basta voltar ao passo do arquivo mantendo o formulário (unidade, escalas, data) preenchido.
  if (currentStep.value === STEP.WARNINGS) {
    qualityWarnings.value = []
    selectedFile.value = null
    currentStep.value = STEP.FILE
    return
  }

  if (currentStep.value > STEP.FORM) {
    currentStep.value--
  } else {
    handleClose()
  }
}

const isStepFormValid = ref(false)

const toast = useToast()
const { notifyError } = useErrorToast()

const formatDateToISO = (date: Date | string | null): string => {
  if (!date) return ''
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

/** Data do registro em dd/MM/yyyy — é como o emissor a lê na folha física. */
const formattedReferenceDate = computed(() => {
  const iso = formatDateToISO(form.value.date)
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  return `${day}/${month}/${year}`
})

const healthUnitIdOrNull = computed(() => {
  const id = Number(form.value.healthUnit)
  return Number.isFinite(id) && id > 0 ? id : null
})

const selectedScheduleIdsAsNumbers = computed(() =>
  form.value.schedules.map(Number).filter((id) => Number.isFinite(id) && id > 0)
)

const buildShiftsBySchedule = (): ScheduleShiftsDto[] => {
  const selectedScheduleIds = new Set(form.value.schedules.map(Number))
  const selectedShiftIds = new Set(form.value.shifts.map(Number))

  if (!selectedShiftIds.size) {
    return Array.from(selectedScheduleIds, (scheduleId) => ({
      scheduleId,
      shiftIds: []
    }))
  }

  const grouped = new Map<number, number[]>()

  for (const shift of listShifts.value) {
    const shiftId = Number(shift.id)
    const sid = Number(shift.schedule.id)

    if (!selectedShiftIds.has(shiftId) || !selectedScheduleIds.has(sid)) continue

    const shiftIds = grouped.get(sid) ?? []
    shiftIds.push(shiftId)
    grouped.set(sid, shiftIds)
  }

  // Assume que todas as escalas selecionadas têm turnos carregados no listShifts.
  // Isso é garantido pelo fluxo normal (fetchShifts carrega turnos das escalas selecionadas),
  // mas verificação defensiva avisa se uma escala selecionada está faltando no store (inconsistência de dados).
  const foundScheduleIds = new Set(grouped.keys())
  const missingSchedules = Array.from(selectedScheduleIds).filter((id) => !foundScheduleIds.has(id))
  if (missingSchedules.length > 0) {
    console.debug(
      `[AttendanceUpload] Escalas selecionadas sem turnos no store: ${missingSchedules.join(', ')}. Isso pode indicar uma inconsistência de dados.`
    )
  }

  return Array.from(grouped, ([scheduleId, shiftIds]) => ({ scheduleId, shiftIds }))
}

/**
 * Extras do Eixo 2A. Só vão quando o passo do checklist existiu: numa modalidade sem folha,
 * um `selfCheck` vazio seria uma declaração que ninguém fez.
 */
const buildUploadExtras = (): DSAttendanceUploadExtras | undefined => {
  if (!needsSelfCheck.value) return undefined
  return {
    selfCheck: buildSelfCheckPayload(selfCheckState.value),
    expectedPages: attachedPages.value
  }
}

/** `schedules` só vem preenchido no envio que sobra depois da nova folha (#3072). */
const uploadEvidence = async (
  schedules: ScheduleShiftsDto[] = buildShiftsBySchedule()
): Promise<DSAttendanceUploadResponse> => {
  const referenceDate = formatDateToISO(form.value.date)
  const healthUnitId = Number(form.value.healthUnit)

  if (form.value.monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP) {
    return await Network.dsAttendance.uploadExternalEvidence(
      selectedFile.value!,
      schedules,
      referenceDate,
      healthUnitId
    )
  }

  const uploadedById = Number(form.value.uploadedBy)
  const imageState = form.value.imageState ?? ImageState.PENDING
  const extras = buildUploadExtras()

  if (form.value.monitoringType === DSAttendanceMonitoringType.NON_DAILY_SHEET) {
    return await Network.dsAttendance.uploadNonDailyEvidence(
      selectedFile.value!,
      schedules,
      referenceDate,
      healthUnitId,
      uploadedById,
      rotation.value,
      imageState,
      extras
    )
  }

  return await Network.dsAttendance.uploadEvidence(
    selectedFile.value!,
    schedules,
    referenceDate,
    healthUnitId,
    uploadedById,
    rotation.value,
    imageState,
    extras
  )
}

const finishUpload = () => {
  emit('save')
  form.value.uploadAnother ? resetModal() : handleClose()
}

/**
 * #3072 — o upload recusou porque um registro da data foi reprovado e não tem substituto. Se a
 * escala desse registro cabe nele (`splitSelectionForNewSheet`), ele vira alvo da nova folha e a
 * escala sai do upload normal. Qualquer dúvida (outra causa, detalhe indisponível) mantém o erro.
 */
const addNewSheetTarget = async (error: unknown): Promise<boolean> => {
  if (form.value.monitoringType !== DSAttendanceMonitoringType.FOLHA) return false
  const containerId = rejectedContainerIdFrom(error)
  if (!containerId || newSheetTargets.value.some((t) => t.containerId === containerId)) return false

  try {
    const record = await Network.dsAttendance.getById(containerId)
    const split = splitSelectionForNewSheet(
      record,
      pendingUploadSchedules.value ?? buildShiftsBySchedule()
    )
    if (!split) return false

    const scheduleName = record.schedule?.name ?? `Escala ${record.schedule?.id ?? ''}`.trim()
    newSheetTargets.value = [...newSheetTargets.value, { containerId, scheduleName, sent: false }]
    pendingUploadSchedules.value = split.remaining
    return true
  } catch (detailError) {
    console.debug('[AttendanceUploadModal] detalhe do registro reprovado indisponível', detailError)
    return false
  }
}

const offerNewSheet = async (error: unknown): Promise<boolean> => {
  if (!(await addNewSheetTarget(error))) return false
  newSheetJustification.value = ''
  currentStep.value = STEP.NEW_SHEET
  return true
}

/** Nova folha em cada alvo ainda não enviado, um por vez. `false` = parou num erro (já avisado). */
const sendPendingNewSheets = async (): Promise<boolean> => {
  const next = newSheetTargets.value.find((target) => !target.sent)
  if (!next) return true
  try {
    await Network.dsAttendance.submitNewSheet(
      next.containerId,
      selectedFile.value!,
      newSheetJustification.value.trim(),
      rotation.value
    )
    next.sent = true
  } catch (error: unknown) {
    notifyError(error, `Não foi possível enviar a nova folha da escala ${next.scheduleName}.`)
    return false
  }
  return sendPendingNewSheets()
}

/**
 * Novas folhas primeiro, depois o upload normal das demais escalas — assim um 409 de "já enviada"
 * numa escala que recebeu a folha numa tentativa anterior não segura o registro reprovado. O 404
 * descreve um par por vez: se o upload achar OUTRO reprovado que caiba, ele vira alvo e o ciclo
 * se repete (no máximo uma vez por escala selecionada).
 */
const completeNewSheetFlow = async (attemptsLeft: number): Promise<boolean> => {
  if (!(await sendPendingNewSheets())) return false

  const remaining = pendingUploadSchedules.value ?? []
  if (normalUploadDone.value || remaining.length === 0) return true

  try {
    await uploadEvidence(remaining)
    normalUploadDone.value = true
    return true
  } catch (error: unknown) {
    if (attemptsLeft > 1 && (await addNewSheetTarget(error))) {
      return completeNewSheetFlow(attemptsLeft - 1)
    }
    notifyError(error, 'Não foi possível enviar a folha para as demais escalas.')
    return false
  }
}

const submitAsNewSheet = async (): Promise<void> => {
  if (!newSheetTargets.value.length || !selectedFile.value || !isNewSheetJustificationValid.value) {
    return
  }
  try {
    isSaving.value = true
    if (!(await completeNewSheetFlow(buildShiftsBySchedule().length))) return
    toast.success(
      newSheetTargets.value.length > 1
        ? 'Nova folha enviada. Os registros voltaram para Aguardando Auditoria.'
        : 'Nova folha enviada. O registro voltou para Aguardando Auditoria.'
    )
    finishUpload()
  } finally {
    isSaving.value = false
  }
}

/**
 * #3083 — mede a qualidade da foto ANTES do envio. Com avisos, mostra o painel com "Refazer a
 * foto" (seguro: nada foi gravado) e "Continuar assim mesmo"; sem avisos, segue para o
 * checklist. A pré-checagem nunca bloqueia: falha ou backend sem a rota viram "sem avisos".
 */
const runQualityPrecheck = async (): Promise<void> => {
  if (!selectedFile.value) return

  let warnings: string[] | null = null
  isSaving.value = true
  try {
    warnings = await Network.dsAttendance.checkEvidenceQuality(
      selectedFile.value,
      attachedPages.value
    )
  } catch (error) {
    console.debug('[AttendanceUploadModal] pré-checagem de qualidade falhou', error)
  } finally {
    isSaving.value = false
  }

  qualityPrecheckRan.value = warnings !== null
  if (warnings?.length) {
    qualityWarnings.value = warnings
    warningsPhase.value = 'BEFORE_SEND'
    currentStep.value = STEP.WARNINGS
    return
  }
  currentStep.value = STEP.CHECKLIST
}

/** Antes do envio, "Continuar assim mesmo" segue para o checklist; depois, "Concluir" fecha. */
const continueFromWarnings = () => {
  if (warningsPhase.value === 'BEFORE_SEND') {
    currentStep.value = STEP.CHECKLIST
    return
  }
  finishUpload()
}

const handleMainAction = async () => {
  if (currentStep.value === STEP.NEW_SHEET) {
    await submitAsNewSheet()
    return
  }

  if (currentStep.value < STEP.PREVIEW) {
    currentStep.value++
    return
  }

  if (currentStep.value === STEP.WARNINGS) {
    continueFromWarnings()
    return
  }

  if (!selectedFile.value) {
    handleClose()
    return
  }

  // Folha física: pré-checagem de qualidade e checklist entre a pré-visualização e o POST.
  if (currentStep.value === STEP.PREVIEW && needsSelfCheck.value) {
    await runQualityPrecheck()
    return
  }

  await sendUpload()
}

/** O POST do envio. Saiu do `handleMainAction` quando o erro passou a abrir a nova folha (#3072). */
const sendUpload = async (): Promise<void> => {
  try {
    isSaving.value = true
    const response = await uploadEvidence()
    toast.success('Upload realizado com sucesso!')
    // Os avisos já foram mostrados ANTES do envio, quando a pré-checagem rodou — a mesma regra
    // do backend daria os mesmos avisos. Só sem pré-checagem (falha de rede, backend antigo) o
    // painel aparece aqui, e apenas informativo: refazer agora cairia no 409 (#3083).
    const warnings = response?.qualityWarnings ?? []
    // O `save` sai uma única vez, no `finishUpload` do "Concluir" — emitir aqui recarregava a
    // lista por baixo do painel de avisos que o emissor ainda está lendo.
    if (warnings.length > 0 && !qualityPrecheckRan.value) {
      qualityWarnings.value = warnings
      warningsPhase.value = 'AFTER_SEND'
      currentStep.value = STEP.WARNINGS
      return
    }
    finishUpload()
  } catch (error: unknown) {
    // `error instanceof Error ? message : fallback` era um funil caseiro: repassava o
    // texto técnico de um TypeError e ignorava o tratamento de rede/timeout/5xx.
    console.error('Erro ao salvar registro:', error)
    if (await offerNewSheet(error)) return
    notifyError(error, 'Não foi possível enviar o registro de presença.')
  } finally {
    isSaving.value = false
  }
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) resetModal()
  }
)
</script>

<style scoped></style>
